import React, { useState } from 'react';
import {
  User,
  Lock,
  Mail,
  Eye,
  EyeOff,
  LogIn,
  UserPlus,
  Heart,
  Activity,
  Shield,
  Phone,
  Calendar,
  AlertCircle
} from 'lucide-react';
import sessionManager from '../../services/SessionManager';
import { apiFetch } from '../../utils/apiClient';

const Login = ({ onLogin }) => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [loginData, setLoginData] = useState({
    emailOrPhone: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  // ------------------ VALIDATIONS --------------------

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone) => /^\d{10}$/.test(phone.replace(/\D/g, ''));

  const checkPasswordStrength = (pass) => {
    if (pass.length < 6) return "Weak";
    if (pass.length < 10) return "Moderate";
    return "Strong";
  };

  // ------------------ INPUT HANDLERS --------------------

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // ------------------ LOGIN API --------------------

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!loginData.emailOrPhone || !loginData.password) {
      setError('Please fill in all fields');
      return;
    }

    const cleanedPhone = loginData.emailOrPhone.replace(/\D/g, '');
    const isPhone = cleanedPhone.length === 10 && !loginData.emailOrPhone.includes('@');
    const isEmail = validateEmail(loginData.emailOrPhone);

    if (!isPhone && !isEmail) {
      setError('Please enter a valid email or phone number');
      return;
    }

    setIsLoading(true);

    try {
      const sessionId = sessionManager.getSessionId();
      const deviceId = sessionManager.getBrowserFingerprint();

      const response = await apiFetch('/backend/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: loginData.emailOrPhone,
          password: loginData.password,
          sessionId,
          deviceId
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Invalid credentials. Please try again.');
        } else if (response.status === 403) {
          setError('Too many failed attempts. Try again later.');
        } else {
          setError(`Login failed (Status ${response.status})`);
        }
        return;
      }

      const data = await response.json();

      if (data.success) {
        sessionManager.saveAuthData({
          user: data.user || null,
          token: data.token,
          session: data.session || null
        });

        setSuccessMessage('Login successful! Redirecting...');
        setTimeout(() => onLogin(data.user || null), 900);
      } else {
        setError(data.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect. Please check your network.');
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------ REGISTER API --------------------

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const { fullName, email, phoneNumber, password, confirmPassword } = registerData;

    if (!fullName || !email || !phoneNumber || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!validateEmail(email)) return setError('Invalid email address');
    if (!validatePhone(phoneNumber)) return setError('Invalid phone number');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setIsLoading(true);

    try {
      const response = await apiFetch('/backend/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Registration successful! Please login.');
        setRegisterData({
          fullName: '',
          email: '',
          phoneNumber: '',
          password: '',
          confirmPassword: ''
        });

        setTimeout(() => {
          setIsLoginMode(true);
          setSuccessMessage('');
        }, 2000);
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------ UI --------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full flex rounded-2xl shadow-2xl overflow-hidden bg-white">

        {/* Left Panel */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-700 p-12 flex-col justify-between text-white">
          <div>
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Apollo Healthcare</h1>
                <p className="text-blue-200">Your Health, Our Priority</p>
              </div>
            </div>

            <div className="space-y-6 mt-12">
              <div className="flex items-start space-x-4">
                <Calendar className="w-6 h-6" />
                <div>
                  <h3 className="font-semibold mb-1">Easy Appointment Booking</h3>
                  <p className="text-blue-200 text-sm">Book appointments in seconds.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Activity className="w-6 h-6" />
                <div>
                  <h3 className="font-semibold mb-1">Health Records</h3>
                  <p className="text-blue-200 text-sm">Secure medical history access.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Shield className="w-6 h-6" />
                <div>
                  <h3 className="font-semibold mb-1">100% Protected</h3>
                  <p className="text-blue-200 text-sm">Your data is encrypted & safe.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-blue-200 text-sm mt-12">© 2025 Apollo Hospitals.</p>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-1/2 p-8 lg:p-12">

          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {isLoginMode ? 'Welcome Back!' : 'Create Account'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isLoginMode ? 'Login to continue' : 'Register to get started'}
          </p>

          {/* ERROR */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* SUCCESS */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
              {successMessage}
            </div>
          )}

          {/* ------------------ LOGIN FORM ------------------ */}
          {isLoginMode ? (
            <form onSubmit={handleLogin} className="space-y-5">

              {/* EMAIL/PHONE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email or Phone</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="emailOrPhone"
                    value={loginData.emailOrPhone}
                    onChange={handleLoginChange}
                    placeholder="Enter email or phone"
                    className="w-full pl-10 pr-4 py-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-12 py-3 border rounded-lg"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>

              {/* LOGIN BUTTON */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                  isLoading ? 'bg-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Login</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            /* ------------------ REGISTER FORM ------------------ */
            <form onSubmit={handleRegister} className="space-y-5">

              {/* NAME */}
              <div>
                <label className="block text-sm font-medium">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="fullName"
                    value={registerData.fullName}
                    onChange={handleRegisterChange}
                    placeholder="Enter your name"
                    className="w-full pl-10 py-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* EMAIL */}
              <div>
                <label className="block text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder="Enter email"
                    className="w-full pl-10 py-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* PHONE */}
              <div>
                <label className="block text-sm font-medium">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={registerData.phoneNumber}
                    onChange={handleRegisterChange}
                    placeholder="10-digit phone number"
                    className="w-full pl-10 py-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-sm font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    placeholder="Min 6 characters"
                    className="w-full pl-10 pr-12 py-3 border rounded-lg"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>

                {/* PASSWORD STRENGTH */}
                {registerData.password && (
                  <p className="text-sm mt-1 text-gray-500">
                    Strength: <span className="font-semibold">{checkPasswordStrength(registerData.password)}</span>
                  </p>
                )}
              </div>

              {/* CONFIRM PASSWORD */}
              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Re-type password"
                    className="w-full pl-10 py-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* REGISTER BUTTON */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                  isLoading ? 'bg-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    <span>Create Account</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* SWITCH LOGIN/REGISTER */}
          <p className="text-center text-gray-600 mt-6">
            {isLoginMode ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => {
                setIsLoginMode(!isLoginMode);
                setError('');
                setSuccessMessage('');
              }}
              className="text-blue-600 font-medium hover:text-blue-700"
            >
              {isLoginMode ? 'Register' : 'Login'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
